import React, { useState } from "react";
import PropTypes from "prop-types";
import { useStyles } from "./styles";
import clsx from "clsx";
import { IconButton, Grid, Typography, Button, Chip } from "@material-ui/core";
import apiClient from "../../../auth/apiClient";
import CreditCardOnFileService from "../../../services/CreditCardOnFileService";
import { useEnqueueSnackbar } from "../../../hooks/useEnqueueSnackbar";
import { useCardOnFile } from "../../../hooks/useCardOnFile";
import DeleteIcon from "@material-ui/icons/Delete";
import Title from "../../Title";
import ColoredLine from "../../ColoredLine";
import CardOnFileButton from "./CardOnFileButton";
import Loading from "../../Loading";
import { CARD_TRANSACTION_STATE } from "../../../constants"

const creditCardOnFileService = new CreditCardOnFileService(apiClient);

const CardOnFileForm = ({ contractID, accessHolderID }) => {
  const enqueueSnackbar = useEnqueueSnackbar();
  const { loading, cardsOnFile, transactionState, hasCardOnFile, refetch, setActiveCard } = useCardOnFile(contractID);
  const [displayMessage, setDisplayMessage] = useState(undefined);
  const classes = useStyles();

  React.useEffect(() => {
    if (!hasCardOnFile) {
      let message;
      switch (transactionState) {
        case CARD_TRANSACTION_STATE.PENDING:
          message = "Card On File in progress... Please wait";
          break;
        case CARD_TRANSACTION_STATE.FAIL:
          message = "Card On File failed... Please retry";
          break;
        default:
          message = undefined;
      }
      setDisplayMessage(message);
    }
  }, [hasCardOnFile, transactionState]);

  const handleDeleteCreditCard = async (recurringCustomerCardID) => {
    try {
      const result = await creditCardOnFileService.deleteCreditCardOnFile(
        contractID,
        recurringCustomerCardID
      );
      if (result.status === 200) {
        refetch();
        enqueueSnackbar("Successfully deleted credit card", {
          variant: "success",
          tag: "configureAcceptedDenomSuccess",
        });
      }
    } catch (err) {
      enqueueSnackbar("Failed to delete credit card", {
        variant: "error",
        tag: "deleteCreditCardError",
      });
      return;
    }
  };

  return (
    <>
      <div className={clsx("cardonfile-form-content", classes.contentRoot)}>
        <Grid
          container
          spacing={2}
          className={clsx("cardonfile-container", classes.cardOnFileContainer)}
        >
          <Grid item xs={9}>
            <div className={clsx("header", classes.header)}>
              <Title className={clsx("cardonfile-title")} noGutter>
                Card on File
              </Title>
            </div>
          </Grid>
          <Grid item xs={3}>
            {transactionState != CARD_TRANSACTION_STATE.PENDING && <CardOnFileButton
              contractID={contractID}
              accessHolderID={accessHolderID}
              cardCount={cardsOnFile ? cardsOnFile.length : 0}
            />}
          </Grid>
        </Grid>
        <ColoredLine />
        {loading ? (
          <div className={clsx("loading-wrapper", classes.loadingWrapper)}>
            <Loading />
          </div>
        ) : cardsOnFile && cardsOnFile.length > 0 ? (
          cardsOnFile.map((card) => (
            <Grid
              key={card.recurringCustomerCardID}
              container
              spacing={2}
              className={clsx("cardonfile-card-container", classes.cardContainer)}
            >
              <Grid item xs={3}>
                <img src={card.cardType.imageSrc} alt={card.cardType.name}></img>
              </Grid>
              <Grid item xs={4}>
                <Typography
                  role="card-on-file-desc"
                  variant="body1"
                  align="left"
                  className={clsx(
                    "cardonfile-card-typo",
                    classes.cardTypoContainer
                  )}
                  component='div'
                >
                  <div xs={12}>
                    {card.maskedCardNumber}
                  </div>
                  <div xs={12}>
                    Exp.{" "} {card.expirationDate}
                  </div>
                </Typography>
              </Grid>
              <Grid item xs={3} className={classes.activeColumn}>
                {card.active ? (
                  <Chip
                    label="Active"
                    className={clsx("active-ribbon", classes.activeRibbon)}
                  />
                ) : (
                  <Button
                    variant="outlined"
                    color="primary"
                    size="small"
                    className={clsx("make-active-btn")}
                    onClick={() => setActiveCard(card.recurringCustomerCardID)}
                  >
                    Make Active
                  </Button>
                )}
              </Grid>
              <Grid item xs={2}>
                <IconButton
                  className={clsx("edit-cardonfile-delete-btn")}
                  color="secondary"
                  variant="contained"
                  onClick={() => handleDeleteCreditCard(card.recurringCustomerCardID)}
                >
                  <DeleteIcon fontSize="large" />
                </IconButton>
              </Grid>
            </Grid>
          ))
        ) : (
          <Typography variant="body1" align="center">
            {displayMessage || "No card on file"}
          </Typography>
        )}
      </div>
    </>
  );
};

CardOnFileForm.propTypes = {
  contractID: PropTypes.string,
  accessHolderID: PropTypes.string,
};

export default CardOnFileForm;
